import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';

import { GroupData } from '@everyboard/games';
import { Coord } from '@everyboard/games';
import { PointyHexaOrientation } from '@everyboard/games';
import { PlayerNumberMap } from '@everyboard/games';
import { Debug } from '@everyboard/games';
import { ScoreName } from '@everyboard/games';
import { GoLegalityInformation } from '@everyboard/games/hexagonal-go';
import { GoMove } from '@everyboard/games/hexagonal-go';
import { GoPhase } from '@everyboard/games/hexagonal-go';
import { GoPiece } from '@everyboard/games/hexagonal-go';
import { GoState } from '@everyboard/games/hexagonal-go';
import { HexagonalGoHeuristic } from '@everyboard/games/hexagonal-go';
import { HexagonalGoMoveGenerator } from '@everyboard/games/hexagonal-go';
import { HexagonalGoConfig, HexagonalGoRules } from '@everyboard/games/hexagonal-go';
import { MGPOptional, MGPValidation, Utils } from '@everyboard/lib';

import { ViewBox } from '../../../components/game-components/GameComponentUtils';
import { HexaLayout } from '../../../components/game-components/HexaLayout';
import { ClickHandler } from '../../../components/game-components/game-component/ClickHandler';
import { HexagonalGameComponent } from '../../../components/game-components/game-component/HexagonalGameComponent';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-hexagonal-go',
    templateUrl: './hexagonal-go.component.html',
    styleUrls: ['../../../components/game-components/game-component/game-component.scss'],
    imports: [NgClass],
})
@Debug.log
export class HexagonalGoComponent extends HexagonalGameComponent<HexagonalGoRules,
                                                                 GoMove,
                                                                 GoState,
                                                                 GoPiece,
                                                                 HexagonalGoConfig,
                                                                 GoLegalityInformation>
{

    public boardInfo: GroupData<GoPiece>;

    public ko: MGPOptional<Coord> = MGPOptional.empty();

    public last: MGPOptional<Coord> = MGPOptional.empty();

    public captures: Coord[]= [];

    public GoPiece: typeof GoPiece = GoPiece;

    public constructor() {
        super('HexagonalGo');
        this.aiConfig = {
            minimax: [{
                id: 'Territory',
                name: $localize`Territory`,
                heuristic: (): HexagonalGoHeuristic => new HexagonalGoHeuristic(),
                moveGenerator: (): HexagonalGoMoveGenerator => new HexagonalGoMoveGenerator(),
            }],
            mcts: [{
                id: 'default',
                name: $localize`MCTS`,
                moveGenerator: (): HexagonalGoMoveGenerator => new HexagonalGoMoveGenerator(),
            }],
        };
        this.encoder = GoMove.encoder;
        this.canPass = true;
        this.scores = MGPOptional.of(PlayerNumberMap.of(0, 0));
        this.setHexaLayout();
    }

    private setHexaLayout(): void {
        const halfStroke: number = this.STROKE_WIDTH / 2;
        const configSize: number = Math.floor(this.state().getWidth() / 2);
        const hexaLayoutStartX: number =
            (- halfStroke * (configSize + 1)) + (Math.sqrt(2) * this.SPACE_SIZE);
        const hexaLayoutStartY: number = this.SPACE_SIZE + halfStroke;
        const hexaLayoutStartingCoord: Coord = new Coord(hexaLayoutStartX, hexaLayoutStartY);
        this.hexaLayout = new HexaLayout(this.SPACE_SIZE,
                                         hexaLayoutStartingCoord,
                                         PointyHexaOrientation.INSTANCE);
    }

    protected override async showLastMove(move: GoMove): Promise<void> {
        this.last = MGPOptional.of(move.coord);
        this.showCaptures();
    }

    public override hideLastMove(): void {
        this.captures = [];
        this.last = MGPOptional.empty();
    }

    protected override computeViewBox(): ViewBox {
        return ViewBox.fromHexa(
            this.state().allCoords(),
            this.hexaLayout,
            this.STROKE_WIDTH,
        )
            .expandAbove(this.SPACE_SIZE)
            .expandBelow(this.SPACE_SIZE);
    }

    @ClickHandler((coord: Coord) => '#click-' + coord.x + '-' + coord.y)
    public async onClick(coord: Coord): Promise<MGPValidation> {
        const resultlessMove: GoMove = new GoMove(coord.x, coord.y);
        return this.chooseMove(resultlessMove);
    }

    public override async updateBoard(_triggerAnimation: boolean): Promise<void> {
        const state: GoState = this.state();
        const phase: GoPhase = state.phase;

        this.hexaBoard = state.getCopiedBoard();
        this.updateScores();

        this.ko = state.koCoord;
        this.canPass = phase.allowsPass();
    }

    private updateScores(): void {
        this.scores = MGPOptional.of(this.state().captured);
    }

    protected override getScoreName(): ScoreName {
        return this.state().phase.getScoreName();
    }

    private showCaptures(): void {
        const previousState: GoState = this.getPreviousState();
        this.captures = [];
        for (const coordAndContent of this.state().getCoordsAndContents()) {
            const coord: Coord = coordAndContent.coord;
            const wasOccupied: boolean = previousState.getPieceAt(coord).isOccupied();
            const isEmpty: boolean = this.hexaBoard[coord.y][coord.x] === GoPiece.EMPTY;
            const isNotKo: boolean = this.ko.equalsValue(coord) === false;
            if (wasOccupied && isEmpty && isNotKo) {
                this.captures.push(coord);
            }
        }
    }

    public override async pass(): Promise<MGPValidation> {
        const phase: GoPhase = this.state().phase;
        if (phase.isPlaying() || phase.isPassed()) {
            return this.onClick(GoMove.PASS.coord);
        }
        Utils.assert(phase.isCounting() || phase.isAccept(),
                     'HexagonalGoComponent: pass() must be called only in playing, passed, counting, or accept phases');
        return this.onClick(GoMove.ACCEPT.coord);
    }

    public getPlayerClassAt(coord: Coord): string[] {
        const piece: GoPiece = this.state().getPieceAt(coord);
        const classes: string[] = [];
        if (this.captures.some((c: Coord) => c.equals(coord))) {
            classes.push('captured-fill');
        }
        if (piece.isOccupied()) {
            classes.push(this.getPlayerClass(piece.player));
        }
        return classes;
    }

    public getTerritoryHexagonalTransform(): string {
        return 'scale(0.6) ';
    }

    public getHexaDiagonalPoints(): string {
        return this.hexaLayout.getHexaDiagonalPoints();
    }

}
